import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  IconButton,
  Paper,
  Chip,
  Slide,
} from "@mui/material";
import {
  Close,
  LinkedIn,
  PersonAdd,
  Description,
  Link as LinkIcon,
} from "@mui/icons-material";
import { blue, grey } from "@mui/material/colors";
import UploadFileDialog from "../../../../components/UploadFileDialog/UploadFileDialog";
import { usePeopleEvidenceUpload } from "../AdminHooks/usePeopleEvidenceUpload";
import { useMutation } from "@apollo/client";
import { UPDATE_EMPLOYEE_PROFILE_METADATA } from "../../../../apis/organization-apis/people-apis/peopleApis";
import { LoadingButton } from "@mui/lab";
import BlinkingStatusDot from "../../../../components/BlinkingStatusDot/BlinkingStatusDot";
import { useFilePolling } from "../AdminHooks/useFilePolling";
import ConfirmSnackbar from "../../../../components/Snackbar/ConfirmSnackbar";
import { EvidenceStatus } from "../../../../utils/enums";

const DividerWithOr = () => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      width: "100%",
      my: 1.5,
    }}
  >
    <Box sx={{ flex: 1, height: "1px", backgroundColor: grey[300] }} />
    <Typography
      sx={{
        px: 2,
        color: "text.secondary",
        fontSize: "0.875rem",
        fontWeight: 500,
      }}
    >
      OR
    </Typography>
    <Box sx={{ flex: 1, height: "1px", backgroundColor: grey[300] }} />
  </Box>
);

const isValidLinkedInUrl = (url) => {
  return /^https:\/\/(www\.)?linkedin\.com\/in\/[a-zA-Z0-9-_]+\/?$/.test(
    url.trim()
  );
};

const UploadDetailsDialog = ({
  open,
  onClose,
  resume,
  setResume,
  linkedInUrl,
  setLinkedInUrl,
  assignedUserId,
  assignedUserEmail,
  refetchResumes,
  refetchMetadata,
  metadataLoading,
}) => {
  const [uploadFileDialogOpen, setUploadFileDialogOpen] = useState(false);
  const check = { checkId: 1101, ownerEmployeeId: assignedUserId };
  const [snackbarInfo, setSnackbarInfo] = useState(null);
  const { isLoading, handlePeopleEvidenceUpload } = usePeopleEvidenceUpload(
    check,
    refetchResumes,
    setSnackbarInfo
  );
  const [updateMetadata, { loading: isUpdating }] = useMutation(
    UPDATE_EMPLOYEE_PROFILE_METADATA
  );
  const hasResume = Boolean(resume);
  const hasLinkedIn =
    typeof linkedInUrl === "string" && linkedInUrl.trim() !== "";
  const [initialLinkedInUrl, setInitialLinkedInUrl] = useState(linkedInUrl);

  const TransitionComponent = (props) => {
    return <Slide {...props} direction="left" />;
  };

  const handleDownloadDocument = (path, maskName) => {
    const link = document.createElement("a");
    link.href = `${process.env.REACT_APP_CLOUDFRONT_URL}/${path}`;
    link.download = maskName || "resume";
    link.click();
  };

  const handleSaveUrl = async () => {
    try {
      if (!assignedUserEmail) {
        return;
      }
      if (!isValidLinkedInUrl(linkedInUrl.trim())) {
        setSnackbarInfo({
          message: "Enter valid LinkedIn url!",
          vertical: "top",
          horizontal: "right",
          Transition: TransitionComponent,
        });
        return;
      }

      // Only save if the LinkedIn URL has changed
      if (linkedInUrl.trim() === (initialLinkedInUrl || "").trim()) {
        onClose();
        return;
      }
      // Save the LinkedIn URL
      const response = await updateMetadata({
        variables: {
          email: assignedUserEmail,
          linkedin_url: linkedInUrl.trim(),
        },
      });
      if (response?.data?.updateEmployeeProfileMetadata?.success) {
        // Refetch metadata
        refetchMetadata();
        setSnackbarInfo({
          message: "LinkedIn URL saved!",
          vertical: "top",
          horizontal: "right",
          Transition: TransitionComponent,
        });

        // onClose();
      } else {
        setSnackbarInfo({
          message: "Failed to save LinkedIn URL! Please try again.",
          vertical: "top",
          horizontal: "right",
          Transition: TransitionComponent,
        });
      }
    } catch (error) {
      setLinkedInUrl("");
    }
  };

  // When resume changes (upload), show loading chip
  const handleFileUpload = async (file) => {
    try {
      const response = await handlePeopleEvidenceUpload(
        file,
        "employee_resume"
      );
      if (response?.uuid) {
        setSnackbarInfo({
          message: "Resume uploaded successfully!",
          vertical: "top",
          horizontal: "right",
          Transition: TransitionComponent,
        });
        setUploadFileDialogOpen(false);
      } else {
        setResume(null);
        setSnackbarInfo({
          message: "Resume upload failed! Please try again.",
          vertical: "top",
          horizontal: "right",
          Transition: TransitionComponent,
        });
      }
    } catch (err) {
      setResume(null);
      setSnackbarInfo({
        message: "Upload failed. Please try again.",
        vertical: "top",
        horizontal: "right",
        Transition: TransitionComponent,
      });
    }
  };

  useEffect(() => {
    if (open) {
      setInitialLinkedInUrl(linkedInUrl);
    }
  }, []);

  const shouldPoll = open && resume?.isFileValid === null;

  useFilePolling(shouldPoll, resume, refetchResumes);

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        fullWidth
        maxWidth="sm"
        sx={{
          "& .MuiDialog-paper": {
            borderRadius: 2,
            boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
          },
        }}
      >
        <DialogTitle sx={{ pb: 2 }}>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
          >
            <Box display="flex" alignItems="center" gap={2}>
              <Box
                sx={{
                  p: 1.5,
                  borderRadius: 1,
                  backgroundColor: blue[50],
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <PersonAdd sx={{ color: blue[600], fontSize: 24 }} />
              </Box>
              <Box>
                <Typography variant="h5" fontWeight={600} color="text.primary">
                  Upload Details
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 0.5 }}
                >
                  Upload either a resume or LinkedIn URL (or both).
                </Typography>
              </Box>
            </Box>
            <IconButton
              onClick={onClose}
              sx={{
                color: grey[500],
                "&:hover": {
                  color: "text.primary",
                  transform: "scale(1.05)",
                },
                transition: "all 0.2s ease",
              }}
            >
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ px: 3, py: 2 }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
            {/* Resume Section */}
            <Paper
              sx={{
                p: 3,
                border: `1px solid ${grey[200]}`,
                borderRadius: 2,
                backgroundColor: "background.paper",
                transition: "all 0.2s ease",
                "&:hover": {
                  borderColor: grey[300],
                  boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                },
              }}
            >
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                mb={2}
              >
                <Box display="flex" alignItems="center" gap={2}>
                  <Box
                    sx={{
                      p: 1.5,
                      borderRadius: 1,
                      backgroundColor: "rgba(76, 175, 80, 0.08)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Description sx={{ color: "#4caf50", fontSize: 20 }} />
                  </Box>
                  <Box>
                    <Typography
                      variant="subtitle1"
                      fontWeight={600}
                      color="text.primary"
                    >
                      Resume
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Upload your latest resume
                    </Typography>
                  </Box>
                </Box>
                {hasResume && (
                  <Box
                    display="flex"
                    flexDirection="column"
                    alignItems="flex-end"
                    gap={1}
                  >
                    <Box
                      display="flex"
                      flexDirection="row"
                      alignItems="center"
                      gap={1}
                    >
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        noWrap
                        maxWidth="200px"
                        sx={{
                          fontSize: "0.875rem",
                          cursor: resume?.path ? "pointer" : "default",
                          textDecoration: resume?.maskName
                            ? "underline"
                            : "none",
                        }}
                        onClick={() => {
                          if (resume?.path && resume?.maskName) {
                            handleDownloadDocument(
                              resume.path,
                              resume.maskName
                            );
                          }
                        }}
                      >
                        {resume?.maskName}
                      </Typography>
                      <BlinkingStatusDot
                        status={
                          resume?.isFileValid === true
                            ? EvidenceStatus.VALID
                            : resume?.isFileValid === false
                              ? EvidenceStatus.INVALID
                              : EvidenceStatus.PROCESSING
                        }
                      />
                    </Box>

                    <Chip
                      label="Uploaded"
                      size="small"
                      sx={{
                        backgroundColor: "rgba(76, 175, 80, 0.08)",
                        color: "#2e7d32",
                        borderRadius: 1,
                        height: 24,
                        fontSize: "0.75rem",
                        fontWeight: 500,
                      }}
                    />
                  </Box>
                )}
              </Box>

              {hasResume ? (
                <Box display="flex" gap={2}>
                  <Button
                    fullWidth
                    variant="outlined"
                    onClick={() => setUploadFileDialogOpen(true)}
                    startIcon={<Description sx={{ fontSize: "0.875rem" }} />}
                    sx={{
                      py: 1.5,
                      flex: 1,
                      borderRadius: 1,
                      fontWeight: 500,
                      textTransform: "none",
                      borderColor: "#4caf50",
                      color: "#2e7d32",
                      fontSize: "0.875rem",
                      "&:hover": {
                        borderColor: "#388e3c",
                        transform: "translateY(-1px)",
                        boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                      },
                      transition: "all 0.2s ease",
                    }}
                  >
                    Change Resume
                  </Button>
                  {/* For future use */}
                  {/* <Button
                    fullWidth
                    variant="outlined"
                    color="error"
                    onClick={() => {
                      setResume(null);
                    }}
                    sx={{
                      py: 1.5,
                      flex: 1,
                      borderRadius: 1,
                      fontWeight: 500,
                      textTransform: 'none',
                      fontSize: '0.875rem',
                      '&:hover': { 
                        transform: 'translateY(-1px)',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                      },
                      transition: 'all 0.2s ease',
                    }}
                  >
                    Delete Resume
                  </Button> */}
                </Box>
              ) : (
                <Button
                  fullWidth
                  variant="contained"
                  onClick={() => setUploadFileDialogOpen(true)}
                  startIcon={<Description sx={{ fontSize: "0.875rem" }} />}
                  sx={{
                    py: 1.5,
                    borderRadius: 1,
                    fontWeight: 500,
                    textTransform: "none",
                    fontSize: "0.875rem",
                    backgroundColor: blue[600],
                    "&:hover": {
                      backgroundColor: blue[700],
                      transform: "translateY(-1px)",
                      boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
                    },
                    transition: "all 0.2s ease",
                  }}
                >
                  Add Resume
                </Button>
              )}
            </Paper>

            <DividerWithOr />

            {/* LinkedIn Section */}
            <Paper
              sx={{
                p: 3,
                border: `1px solid ${grey[200]}`,
                borderRadius: 2,
                backgroundColor: "background.paper",
                transition: "all 0.2s ease",
                "&:hover": {
                  borderColor: grey[300],
                  boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                },
              }}
            >
              <Box display="flex" alignItems="center" gap={2} mb={2}>
                <Box
                  sx={{
                    p: 1.5,
                    borderRadius: 1,
                    backgroundColor: "rgba(25, 118, 210, 0.08)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <LinkedIn sx={{ color: blue[600], fontSize: 20 }} />
                </Box>
                <Box>
                  <Typography
                    variant="subtitle1"
                    fontWeight={600}
                    color="text.primary"
                  >
                    LinkedIn Profile
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Add your LinkedIn profile URL
                  </Typography>
                </Box>
              </Box>

              <TextField
                fullWidth
                label="LinkedIn Profile URL      "
                placeholder="https://linkedin.com/in/your-profile"
                value={linkedInUrl}
                onChange={(e) => setLinkedInUrl(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <LinkIcon
                      sx={{ color: grey[500], mr: 1, fontSize: "1rem" }}
                    />
                  ),
                }}
                variant="outlined"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 1,
                    fontSize: "0.875rem",
                    "&:hover": {
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: blue[400],
                      },
                    },
                  },
                }}
              />
            </Paper>
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 3, pt: 2, gap: 1 }}>
          <Button
            onClick={onClose}
            variant="outlined"
            sx={{
              px: 3,
              py: 1,
              color: grey[600],
              borderColor: grey[300],
              borderRadius: 1,
              textTransform: "none",
              fontWeight: 500,
              fontSize: "0.875rem",
              "&:hover": {
                borderColor: grey[400],
                transform: "translateY(-1px)",
                boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
              },
              transition: "all 0.2s ease",
            }}
          >
            Cancel
          </Button>
          <LoadingButton
            onClick={async () => {
              if (hasLinkedIn) {
                await handleSaveUrl();
              }
            }}
            variant="contained"
            disabled={!hasResume && !hasLinkedIn}
            loading={metadataLoading || isUpdating}
            sx={{
              px: 3,
              py: 1,
              borderRadius: 1,
              textTransform: "none",
              fontWeight: 500,
              fontSize: "0.875rem",
              minWidth: 90,
              minHeight: 41,
              backgroundColor: blue[600],
              "&:hover": {
                backgroundColor: blue[700],
                transform: "translateY(-1px)",
                boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
              },
              "&:disabled": {
                backgroundColor: grey[300],
                color: grey[500],
              },
              transition: "all 0.2s ease",
            }}
          >
            {isUpdating || metadataLoading ? "" : "Save"}
          </LoadingButton>
        </DialogActions>
      </Dialog>

      {/* Resume Upload File Dialog */}
      <UploadFileDialog
        open={uploadFileDialogOpen}
        onClose={() => setUploadFileDialogOpen(false)}
        onFileUpload={handleFileUpload}
        allowedFileTypes={[".pdf", ".doc", ".docx"]}
        dialogTitle="Upload Resume"
        uploadButtonText={isLoading ? "Uploading..." : "Upload"}
      />
      <ConfirmSnackbar snackbarInfo={snackbarInfo} />
    </>
  );
};

export default UploadDetailsDialog;
